/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-restricted-syntax */
import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';

const {
  CKEditor: {
    Plugin,
    Editor: _Editor,
    Range: _Range,
    DocumentFragment: _DocumentFragment,
    Element: _Element,
    Position: _Position,
  },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

type Editor = InstanceType<typeof _Editor>;
type Range = InstanceType<typeof _Range>;
type DocumentFragment = InstanceType<typeof _DocumentFragment>;
type Element = InstanceType<typeof _Element>;
type Position = InstanceType<typeof _Position>;
interface ElementTextNode {
  text: string | null;
  item: Item | DocumentFragment;
  children: ElementTextNode[];
}

type Item = Parameters<Range['containsItem']>[0];
/**
 * Utility plugin for AgentChatPlugin that provides common functionality
 * and helper methods for agent chat operations.
 */
export class AgentChatUtilPlugin extends Plugin {
  static get pluginName() {
    return 'AgentChatUtilPlugin';
  }

  private createFragment(html: string) {
    const editor = this.editor as Editor;
    const viewFragment = editor.data.processor.toView(html);
    const modelFragment = editor.data.toModel(viewFragment);
    return modelFragment;
  }

  public createRangeForHTML(html: string): Range | null {
    const root = this.editor.model.document.getRoot();
    if (!root) return null;

    const rootTree = this.createTextElementTree(root);
    const fragment = this.createFragment(html);
    const fragmentTree = this.createTextElementTree(fragment);

    const [startIndex, endIndex] = this.findMatch(rootTree, fragmentTree);

    if (startIndex === -1) {
      return null;
    }

    const startPosition = this.getPositionForTextIndexBefore(
      rootTree,
      startIndex,
    );
    const endPosition = this.getPositionForTextIndexAfter(rootTree, endIndex);

    if (!startPosition || !endPosition) {
      return null;
    }

    return this.editor.model.createRange(startPosition, endPosition);
  }

  private findMatch(
    srcTree: ElementTextNode,
    searchTree: ElementTextNode,
  ): [number, number] {
    const srcTreeText = this.elementTextNodeToText(srcTree);
    const searchTreeText = this.elementTextNodeToText(searchTree);
    const index = srcTreeText.indexOf(searchTreeText);
    return [index, index + searchTreeText.length];
  }

  /**
   * Finds the model position corresponding to an index in the editor's plain text representation.
   * The plain text representation is assumed to be generated by `AgentChatUtilPlugin.rangeToText`
   * applied to the whole document (where non-text elements contribute a newline character).
   *
   * @param textIndex The index within the editor's plain text representation.
   * @param mode Whether the position should be 'before' or 'after' the character at `textIndex`.
   * @returns A model.Position object, or null if the index is out of bounds or an error occurs.
   * The position aims to be "as high in the tree as possible" when `textIndex` corresponds
   * to an element boundary.
   */
  public getPositionForTextIndexBefore(
    tree: ElementTextNode,
    textIndex: number,
  ): Position | null {
    const text = this.elementTextNodeToText(tree);
    if (textIndex < 0 || textIndex > text.length) {
      console.warn(`Text index ${textIndex} is out of bounds for text ${text}`);
      return null;
    }

    const childrenTexts = tree.children.map((c) =>
      this.elementTextNodeToText(c),
    );

    let currentIndex = 0;
    let currentOffset = 0;

    for (let i = 0; i < childrenTexts.length; i += 1) {
      const child = tree.children[i];
      const childText = childrenTexts[i];

      const indexContribution =
        childText.length + (i === childrenTexts.length - 1 ? 0 : 1);
      // If it is a perfect match we can just return the position
      if (currentIndex === textIndex) {
        return this.editor.model.createPositionAt(
          child.item.parent!,
          currentOffset,
        );
      }

      // If the text index is within the current child's text, we need to recurse
      if (
        currentIndex < textIndex &&
        textIndex < currentIndex + indexContribution
      ) {
        return this.getPositionForTextIndexBefore(
          child,
          textIndex - currentIndex,
        );
      }

      currentIndex += indexContribution;
      if (!child.item.is('documentFragment')) {
        currentOffset += child.item.offsetSize;
      }
    }

    // The text index in in this child

    if (!tree.item.parent) {
      return this.editor.model.createPositionAt(tree.item, textIndex);
    }
    return this.editor.model.createPositionAt(tree.item.parent, textIndex);
  }

  public getPositionForTextIndexAfter(
    tree: ElementTextNode,
    textIndex: number,
  ): Position | null {
    const text = this.elementTextNodeToText(tree);
    if (textIndex < 0 || textIndex > text.length) {
      console.warn(`Text index ${textIndex} is out of bounds for text ${text}`);
      return null;
    }
    const childrenTexts = tree.children.map((c) =>
      this.elementTextNodeToText(c),
    );

    let currentIndex = 0;
    let currentOffset = 0;

    for (let i = 0; i < childrenTexts.length; i += 1) {
      const child = tree.children[i];
      const childText = childrenTexts[i];

      const indexContribution =
        childText.length + (i === childrenTexts.length - 1 ? 0 : 1);
      currentIndex += indexContribution;
      if (!child.item.is('documentFragment')) {
        currentOffset += child.item.offsetSize;
      }

      // If it is a perfect match we can just return the position

      if (currentIndex === textIndex) {
        let { parent } = child.item;
        let offsetForParent = currentOffset;

        while (parent?.parent && offsetForParent === currentOffset) {
          offsetForParent = parent?.endOffset ?? 0;
          parent = parent.parent;
        }

        return this.editor.model.createPositionAt(parent!, offsetForParent);
      }

      // If the text index is within the current child's text, we need to recurse
      if (
        currentIndex - indexContribution < textIndex &&
        textIndex < currentIndex
      ) {
        return this.getPositionForTextIndexAfter(
          child,
          textIndex - currentIndex + indexContribution,
        );
      }
    }

    if (!tree.item.parent) {
      return this.editor.model.createPositionAt(tree.item, textIndex);
    }
    return this.editor.model.createPositionAt(tree.item.parent, textIndex);
  }

  public elementTextNodeToText(elementTextNode: ElementTextNode): string {
    const nodeText = elementTextNode.text;

    if (!nodeText) {
      return elementTextNode.children
        .map(this.elementTextNodeToText.bind(this))
        .join('\n');
    }

    const childrenText = elementTextNode.children
      .map(this.elementTextNodeToText.bind(this))
      .join('\n');

    if (childrenText.length > 0) {
      return `${nodeText}\n${childrenText}`;
    }
    return nodeText;
  }

  public createTextElementTree(
    element: Element | DocumentFragment,
  ): ElementTextNode {
    const elementText = AgentChatUtilPlugin.getTextForItem(element);
    const children = Array.from(element.getChildren());

    const childrenTexts: ElementTextNode[] = [];
    for (const child of children) {
      if (child.is('element')) {
        const childText = this.createTextElementTree(child);
        childrenTexts.push(childText);
      } else {
        const childText = AgentChatUtilPlugin.getTextForItem(child);
        childrenTexts.push({
          text: childText,
          item: child,
          children: [],
        });
      }
    }

    return {
      text: elementText,
      item: element,
      children: childrenTexts,
    };
  }

  public static getTextForItem(
    item: Item | Element | DocumentFragment,
  ): string | null {
    // Properly check the node type with type guard
    if (!(item.is && (item.is('$text') || item.is('$textProxy')))) {
      // Editor has only one inline element defined in schema: `<softBreak>` which is treated as new line character in blocks.
      // Special handling might be needed for other inline elements (inline widgets).
      return null;
    }

    // At this point TypeScript knows node has a 'data' property
    return item.data;
  }
}
